import React, { useState } from 'react'
import { useParams } from 'react-router-dom'

import objectUtils from '../../_helpers/objectUtils'
import reactQuery from '../../_helpers/reactQuery'
import schemaUtils from '../../_helpers/schemaUtils'
import translateUtils from '../../_helpers/translateUtils'
import { filterService } from '../../_services'
import filterSchema from '../../schemas/filter.schema'
import AbstractLayout from './AbstractLayout'

const AbstractDocument = function ({
  singleCollectionName,
  notToShow,
  update = true,
  id,
}) {
  const queryKey = 'filters'
  const singleQueryKey = 'filter'
  const params = useParams()
  // Static routes like /pages/homepage have no :id URL segment — the caller
  // resolves the Mongo _id itself and passes it in. Param routes keep the
  // old behavior.
  const docId = id || params.id
  const getQuery = reactQuery.getOneQuery(
    singleQueryKey,
    docId,
    filterService.get
  )
  const updateMutation = reactQuery.updateMutation(
    filterService.update,
    docId,
    singleCollectionName,
    singleQueryKey,
    queryKey
  )
  const deleteMutation = reactQuery.deleteMutation(
    filterService.remove,
    docId,
    singleCollectionName,
    singleQueryKey,
    queryKey
  )
  const appendImagesMutation = reactQuery.updateMutation(
    filterService.addImages,
    docId,
    singleCollectionName,
    singleQueryKey,
    queryKey
  )
  const prependImagesMutation = reactQuery.updateMutation(
    filterService.addImages,
    docId,
    singleCollectionName,
    singleQueryKey,
    queryKey,
    { prepend: true }
  )
  const [body, setBody] = useState({})
  const action = (actionName, mutation) => ({
    buttonLabel: `${translateUtils.translate(
      actionName
    )} ${translateUtils.translate('Images')}`,
    buttonColor: 'primary',
    onSubmit: async (args) => {
      const promises = Object.keys(args.images).map((key) => {
        const formData = new FormData()
        formData.append('images', args.images[key])
        // Phase 4 (completed post-audit): alt collected at upload time. One
        // value per batch — each file goes out as its own request carrying
        // it; the per-image edit dialog stays the place to fine-tune. The
        // server falls back to the filter name when these are absent.
        if (args.imageAlt) formData.append('imageAlt', args.imageAlt)
        if (args.imageAltAr) formData.append('imageAltAr', args.imageAltAr)
        return new Promise((resolve, reject) => {
          mutation.mutate(formData, {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          })
        })
      })
      await Promise.all(promises)
    },
    body,
    setBody,
    withClear: true,
    clearFunction: () => setBody({}),
    form: {
      title: `${translateUtils.translate(
        actionName
      )} ${translateUtils.translate('Images')}`,
      inputs: schemaUtils.schemaToInputsArray(
        {
          images: filterSchema.images,
          // Local to this dialog on purpose — putting imageAlt into the
          // shared filter schema would leak it into every other edit form.
          imageAlt: { type: 'string', maxLength: 125 },
          imageAltAr: { type: 'string', maxLength: 125 },
        },
        getQuery.data,
        false,
        undefined,
        ['images', 'imageAlt', 'imageAltAr']
      ),
    },
  })
  return (
    <AbstractLayout
      actions={[
        ...(update
          ? [
              ...(!notToShow.includes('images')
                ? [
                    action('Prepend', prependImagesMutation),
                    action('Append', appendImagesMutation),
                  ]
                : []),
              {
                buttonLabel: `${translateUtils.translate(
                  'Update'
                )} ${translateUtils.translate(singleCollectionName)}`,
                buttonColor: 'primary',
                onSubmit: (args) => {
                  const newBody = objectUtils.compare(getQuery.data, args)
                  const { coverPhoto, images } = newBody
                  delete newBody.coverPhoto
                  delete newBody.images
                  const formData = objectUtils.getFormData(newBody)
                  if (coverPhoto && coverPhoto.length)
                    Object.keys(coverPhoto).forEach((key) =>
                      formData.append('coverPhoto', coverPhoto[key])
                    )
                  if (images && images.length)
                    Object.keys(images).forEach((key) =>
                      formData.append('images', images[key])
                    )
                  if (
                    Object.keys(newBody).length ||
                    (coverPhoto && coverPhoto.length) ||
                    (images && images.length)
                  )
                    return updateMutation.mutate(formData)
                },
                body,
                setBody,
                withClear: true,
                clearFunction: () => setBody({}),
                form: {
                  title: `${translateUtils.translate(
                    'Update'
                  )} ${translateUtils.translate(singleCollectionName)}`,
                  inputs: schemaUtils.schemaToInputsArray(
                    filterSchema,
                    getQuery.data,
                    false,
                    notToShow
                  ),
                },
              },
            ]
          : []),
        {
          buttonLabel: `${translateUtils.translate(
            'Delete'
          )} ${translateUtils.translate(singleCollectionName)}`,
          buttonColor: 'secondary',
          onSubmit: () => deleteMutation.mutate(),
          form: {
            title: `${translateUtils.translate(
              'Delete'
            )} ${translateUtils.translate(singleCollectionName)}`,
          },
          confirm: true,
          info: `${translateUtils.translate(
            'Are you sure you want to delete this'
          )} ${translateUtils.translate(singleCollectionName)}`,
        },
      ]}
      title={singleCollectionName}
      getQuery={getQuery}
      many={false}
      notToShow={notToShow}
      navigate={['images', docId, '_id']}
    />
  )
}
export default AbstractDocument
